import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import EmberCanvas from "./EmberCanvas";
import { MaskedLine, Eyebrow } from "./Reveal";
import { SITE, COUNTDOWN_TARGET, bnNum } from "@/constants/site";
import { useLang } from "@/lib/lang";

function useCountdown(target) {
  const calc = () => {
    const diff = Math.max(0, target.getTime() - Date.now());
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      mins: Math.floor((diff % 3600000) / 60000),
      secs: Math.floor((diff % 60000) / 1000),
      done: diff === 0,
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const iv = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(iv);
  }, []);
  return t;
}

function Countdown() {
  const t = useCountdown(COUNTDOWN_TARGET);
  const { lang, t: tr } = useLang();
  const cells = [
    { v: t.days, k: "cd_days" },
    { v: t.hours, k: "cd_hours" },
    { v: t.mins, k: "cd_mins" },
    { v: t.secs, k: "cd_secs" },
  ];
  return (
    <div className="flex gap-3 sm:gap-5" data-testid="hero-countdown">
      {cells.map((c) => {
        const num = String(c.v).padStart(2, "0");
        return (
          <div key={c.k} className="min-w-[72px] sm:min-w-[88px] rounded-2xl border border-[#F2B134]/25 bg-[#1E0A0E]/70 backdrop-blur px-3 py-3 text-center">
            <p className="font-bengali text-2xl sm:text-3xl font-bold gold-text tabular-nums">{lang === "bn" ? bnNum(num) : num}</p>
            <p className="text-[10px] tracking-[0.2em] text-[#D1BBA7]/80 mt-0.5">{tr(c.k)}</p>
          </div>
        );
      })}
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const { lang, t } = useLang();
  const titleFont = lang === "en" ? "font-display tracking-tight" : "font-bengali";

  const go = (id) => (e) => {
    e.preventDefault();
    if (window.__lenis) window.__lenis.scrollTo(`#${id}`, { offset: -70 });
    else document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={ref} id="home" className="relative min-h-[100svh] flex items-end overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src="/hero.jpg" alt="দুর্গা প্রতিমা ও ধুনুচি আরতি" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-[radial-gradient(80%_60%_at_50%_35%,transparent_0%,rgba(18,5,7,0.55)_70%,rgba(18,5,7,0.92)_100%)]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#120507] via-transparent to-[#120507]/70" />
      </motion.div>

      <EmberCanvas density={44} />

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 pt-36 pb-14 sm:pb-20">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.35 }} className="flex flex-wrap items-center gap-3 mb-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#F2B134]/35 bg-[#120507]/60 backdrop-blur px-4 py-1.5">
            <img src="/logo.jpg" alt="" className="h-5 w-5 rounded-full object-cover" />
            <span className="text-[11px] uppercase tracking-[0.28em] text-[#F2B134] font-semibold">{t("hero_theme")}</span>
          </span>
          <Eyebrow>{t("hero_est")}</Eyebrow>
        </motion.div>

        <h1 className={`${titleFont} font-extrabold leading-[1.05] text-[#FDF6EA] text-5xl sm:text-6xl lg:text-7xl xl:text-8xl max-w-4xl`}>
          <MaskedLine delay={0.45}>{t("hero_title1")}</MaskedLine>
          <MaskedLine delay={0.6}>
            <span className="gold-text">{t("hero_title2")}</span>
          </MaskedLine>
        </h1>

        <MaskedLine delay={0.75} className="mt-5">
          <p className={`text-[#D1BBA7] text-base sm:text-lg max-w-2xl ${lang === "en" ? "" : "font-bengali"}`}>
            {t("hero_sub")}
          </p>
        </MaskedLine>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.9 }} className="mt-9">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#F2B134]/80 mb-3">{t("hero_countdown")}</p>
          <Countdown />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 1.05 }} className="mt-9 flex flex-wrap gap-4">
          <a href="#schedule" onClick={go("schedule")} data-testid="hero-schedule-btn" className="btn-gold rounded-full px-7 py-3.5 text-sm font-bold">
            {t("hero_schedule_btn")}
          </a>
          <a href="#donate" onClick={go("donate")} data-testid="hero-donate-btn" className="btn-crimson rounded-full px-7 py-3.5 text-sm font-bold">
            {t("hero_donate_btn")}
          </a>
        </motion.div>
      </motion.div>

      <motion.a
        href="#heritage"
        onClick={go("heritage")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 right-6 z-10 hidden sm:grid place-items-center h-12 w-12 rounded-full border border-[#F2B134]/30 text-[#F2B134]"
        aria-label={t("hero_scroll")}
      >
        <ChevronDown size={18} className="animate-bounce" />
      </motion.a>
    </section>
  );
}
